import { AnyAction } from 'redux';
import * as actionTypes from '../../../models/actionTypes';
import { ContactUsHistoryTag } from '../../../models/constants/constants';
import { initialNavState, INavState, UITabViewInfo, UITabView, UITabViewMode } from '../../../models/reduxState';

// Triggered on iFrame OnLoad event to catch inner and outer iframe navigation
const handleHistoryUpdate = (state: INavState = initialNavState, action: AnyAction) => {
  // When back button is pressed historyPop handles the history update, which can trigger an iframe update which then triggers the history update which will reset the backButton flag
  if (state.backButtonHit) {
    return {
      ...state,
      backButtonHit: false,
    };
  } else if (!state.userSearched) {
    // Handle History update when user is navigating within IFrame in Contact Support flow
    return {
      ...state,
      history: [...state.history, action.href],
      contactIframeSrc: action.href,
    };
  } else {
    // Handle History update when user searches from navbar in search flow or clicks an article from contact support home page
    return {
      ...state,
      userSearched: false,
    };
  }
};

const handleHistoryPop = (state: INavState = initialNavState, _action: AnyAction) => {
  if (state.history[state.history.length - 1] !== ContactUsHistoryTag) {
    if (state.history[state.history.length - 2] === ContactUsHistoryTag) {
      // Situation in which user is in contact support flow and back navigating from an article to contact us home page
      return {
        ...state,
        backButtonHit: true,
        history: [...state.history.slice(0, state.history.length - 1)],
        inContactArticle: false,
        contactIframeSrc: null,
      };
    } else {
      // Situation in which user is in contact support flow and back navigating from an article to the previously viewed article
      return {
        ...state,
        backButtonHit: true,
        history: [...state.history.slice(0, state.history.length - 1)],
        inContactArticle: true,
        contactIframeSrc: state.history[state.history.length - 2],
      };
    }
  } else {
    // Situation in which user is in search flow or home page of contact support flow and back navigating to the previously viewed article in search flow (could be default home page)
    return {
      ...state,
      backButtonHit: true,
      history: [...state.history.slice(0, state.history.length - 1)],
      inContactArticle: false,
    };
  }
};

const toggleBackButton = (state: INavState = initialNavState, action: AnyAction) => {
  if (action.disabled === true) {
    return {
      ...state,
      backButtonDisabled: true,
    };
  } else {
    return {
      ...state,
      backButtonDisabled: false,
    };
  }
};

const hideRequestCreationTab = (state: INavState = initialNavState, action: AnyAction) => {
  return {
    ...state,
    requestCreationTabHidden: action.isHidden,
  };
};

const handleContactArticleClick = (state: INavState = initialNavState, action: AnyAction) => {
  return {
    ...state,
    contactIframeSrc: action.articleUrl,
    inContactArticle: true,
    userSearched: true,
    backButtonHit: false,
    history: [...state.history, action.articleUrl],
  };
};

const handleModalityExperienceInIframe = (state: INavState = initialNavState, action: AnyAction) => {
  return {
    ...state,
    contactIframeSrc: action.iframeModalityLink,
    inContactArticle: false,
    backButtonHit: false,
  };
};

const updateUITabViewInfo = (state: INavState = initialNavState, action: AnyAction) => {
  let uITabViewStack = [...state.uITabViewStack];
  if (uITabViewStack && uITabViewStack.length > 0) {
    const lastUITabView = uITabViewStack[uITabViewStack.length - 1];
    const uITabViewInfo = action.uITabViewInfo as UITabViewInfo;
    const uITabView = uITabViewInfo ? uITabViewInfo.uITabView : '';
    // request was created, remove requestCreation tab and set previous tab to searchHelp
    if (action.requestCreated && lastUITabView.uITabView === UITabView.requestCreation) {
      uITabViewStack.pop();
    }
    // no need to add the tab, if we're on it already
    if (lastUITabView.uITabView !== uITabView) {
      uITabViewStack.push(action.uITabViewInfo);
    } else if (uITabView === UITabView.requestDetails) {
      // Update viewMode if it needs to be updated
      const currentTabViewInfo = !!lastUITabView ? { ...lastUITabView } : null;
      const viewMode = action.uITabViewInfo.viewMode || UITabViewMode.readOnly;
      if (!!currentTabViewInfo && currentTabViewInfo.viewMode !== viewMode) {
        currentTabViewInfo.viewMode = viewMode;
        uITabViewStack[uITabViewStack.length -1] = currentTabViewInfo;
      }
    }
  } else {
    // stack is empty
      uITabViewStack = [action.uITabViewInfo];
  }
  return {
    ...state,
    uITabViewStack: uITabViewStack,
  };
};

const handlePopUITabViewStack = (state: INavState = initialNavState) => {
  return {
    ...state,
    uITabViewStack: state.uITabViewStack ? state.uITabViewStack.slice(0, state.uITabViewStack.length - 1) : [new UITabViewInfo()],
    // boolean that tells if upcoming top item on stack has url or not
    inContactArticle: state.uITabViewStack && !!(state.uITabViewStack[state.uITabViewStack.length - 2]?.url),
  };
};

const resetPageStack = (state: INavState): INavState => {
  if (state.inContactArticle) {
    // If user is in the InAppReader page, reset the UITabViewInfo stack, but keep the last one so user stays in the InAppReader page.
    return { ...state, uITabViewStack: [new UITabViewInfo(), state.uITabViewStack[state.uITabViewStack.length - 1]] };
  } else {
    // For other situation, just reset the UITabViewInfo stack to send user back to search page.
    return { ...state, uITabViewStack: [new UITabViewInfo()] };
  }
};

export const NavReducer = (state: INavState = initialNavState, action: any) => {
  switch (action.type) {
    case actionTypes.DISABLE_BACK_BUTTON:
      return toggleBackButton(state, action);
    case actionTypes.ADD_TO_HISTORY:
      return handleHistoryUpdate(state, action);
    case actionTypes.POP_HISTORY:
      return handleHistoryPop(state, action);
    case actionTypes.CONTACT_ARTICLE_CLICKED:
      return handleContactArticleClick(state, action);
    case actionTypes.SHOW_MODALITY_EXPERIENCE_IN_IFRAME:
      return handleModalityExperienceInIframe(state, action);
    case actionTypes.UPDATE_UITABVIEWINFO:
      return updateUITabViewInfo(state, action);
    case actionTypes.POP_UITABVIEWSTACK:
      return handlePopUITabViewStack(state);
    case actionTypes.USER_SIGN_OUT:
    case actionTypes.RESET_PAGE_STACK:
      return resetPageStack(state);
    case actionTypes.HIDE_REQUEST_CREATION_TAB:
      return hideRequestCreationTab(state, action);
    default:
      return state;
  }
};
